import { PayloadAction } from '@reduxjs/toolkit'
import {
  IAIImageState,
  IAIImageModel,
  IAIImageStyle,
  IAIImageProject,
  IGenerateFormState,
  IReferenceImage
} from 'store/types/aiImage'
import { IVersionHistoryItem } from 'store/types/aiVideo'
import { getInitialGenerateFormState } from 'store/features/aiImage/initialState'
import { renumberMentionsAfterRemoval } from 'utils/promptMentions'

const reducers = {
  // Generating reducers
  setIsRequestingGeneration: (
    state: IAIImageState,
    action: PayloadAction<boolean>
  ) => {
    state.isRequestingGeneration = action.payload
  },

  // Ref images uploading reducers
  setIsRefImagesUploading: (
    state: IAIImageState,
    action: PayloadAction<boolean>
  ) => {
    state.isRefImagesUploading = action.payload
  },

  // Models reducers
  setModelsIsLoading: (
    state: IAIImageState,
    action: PayloadAction<boolean>
  ) => {
    state.models.isLoading = action.payload
  },

  setModels: (state: IAIImageState, action: PayloadAction<IAIImageModel[]>) => {
    state.models.data = action.payload
  },

  // Styles reducers
  setStylesIsLoading: (
    state: IAIImageState,
    action: PayloadAction<boolean>
  ) => {
    state.styles.isLoading = action.payload
  },

  setRestylingStylesIsLoading: (
    state: IAIImageState,
    action: PayloadAction<boolean>
  ) => {
    state.restylingStyles.isLoading = action.payload
  },

  setRestylingStyles: (
    state: IAIImageState,
    action: PayloadAction<IAIImageStyle[]>
  ) => {
    state.restylingStyles.data = action.payload
  },

  setStyles: (state: IAIImageState, action: PayloadAction<IAIImageStyle[]>) => {
    state.styles.data = action.payload
  },

  // History reducers
  setHistoryIsLoading: (
    state: IAIImageState,
    action: PayloadAction<boolean>
  ) => {
    state.history.isLoading = action.payload
  },

  setHistoryData: (
    state: IAIImageState,
    action: PayloadAction<IAIImageProject[]>
  ) => {
    state.history.data = action.payload
  },

  setHistoryCount: (state: IAIImageState, action: PayloadAction<number>) => {
    state.history.totalCount = action.payload
  },

  resetHistoryPagination: (state: IAIImageState) => {
    state.history.data = null
    state.history.totalCount = null
    state.history.isLoading = false
  },

  setIsHistoryScopedToCurrentProject: (
    state: IAIImageState,
    action: PayloadAction<boolean>
  ) => {
    state.history.isScopedToCurrentProject = action.payload
  },

  // Delete reducers
  setDeleteItemId: (state: IAIImageState, action: PayloadAction<number>) => {
    state.deleteItemIds = [...(state.deleteItemIds || []), action.payload]
  },

  removeDeleteItemId: (state: IAIImageState, action: PayloadAction<number>) => {
    state.deleteItemIds =
      state.deleteItemIds?.filter(id => id !== action.payload) || null
  },

  // Generate form reducers
  setGenerateForm: (
    state: IAIImageState,
    action: PayloadAction<IGenerateFormState>
  ) => {
    state.generateForm = action.payload
  },

  updateGenerateForm: (
    state: IAIImageState,
    action: PayloadAction<Partial<IGenerateFormState>>
  ) => {
    state.generateForm = { ...state.generateForm, ...action.payload }
  },

  updateGenerationForm: (
    state: IAIImageState,
    action: PayloadAction<Partial<IGenerateFormState>>
  ) => {
    const updatedForm = { ...state.generateForm, ...action.payload }
    state.generateForm = updatedForm
  },
  setGenerationForm: (
    state: IAIImageState,
    action: PayloadAction<IGenerateFormState>
  ) => {
    state.generateForm = action.payload
  },

  setModelCode: (state: IAIImageState, action: PayloadAction<string>) => {
    state.generateForm.modelCode = action.payload
  },

  setPrompt: (state: IAIImageState, action: PayloadAction<string>) => {
    state.generateForm.prompt = action.payload
  },

  setAspectRatio: (state: IAIImageState, action: PayloadAction<string>) => {
    state.generateForm.aspectRatio = action.payload
  },

  setStyleId: (
    state: IAIImageState,
    action: PayloadAction<number | undefined>
  ) => {
    state.generateForm.styleId = action.payload
  },

  setTargetMediaItemId: (
    state: IAIImageState,
    action: PayloadAction<string | null>
  ) => {
    state.generateForm.targetMediaItemId = action.payload
  },

  setReferenceImages: (
    state: IAIImageState,
    action: PayloadAction<IReferenceImage[]>
  ) => {
    state.generateForm.refImages = action.payload
  },

  removeReferenceImage: (
    state: IAIImageState,
    action: PayloadAction<number>
  ) => {
    // Prompt @mentions are POSITIONAL (`@Image N` = 1-based index in
    // refImages) while removal is by id — so resolve the index BEFORE
    // filtering, then renumber in the same reducer. Prompt and refImages live
    // in this slice together, so the two can never drift apart.
    const removedIndex = state.generateForm.refImages.findIndex(
      image => image.id === action.payload
    )

    state.generateForm.refImages = state.generateForm.refImages.filter(
      image => image.id !== action.payload
    )

    if (removedIndex !== -1) {
      state.generateForm.prompt = renumberMentionsAfterRemoval(
        state.generateForm.prompt,
        removedIndex + 1
      )
    }
  },

  setActiveTab: (state: IAIImageState, action: PayloadAction<string>) => {
    state.activeTab = action.payload
  },

  resetGenerateForm: (state: IAIImageState) => {
    const resetFormData = getInitialGenerateFormState()

    state.generateForm = resetFormData
  },

  setVersionHistoryLoading: (
    state: IAIImageState,
    action: PayloadAction<boolean>
  ) => {
    state.versionHistory.isLoading = action.payload
  },
  setVersionHistoryItems: (
    state: IAIImageState,
    action: PayloadAction<IVersionHistoryItem[]>
  ) => {
    state.versionHistory.items = action.payload
    state.versionHistory.isLoaded = true
  },
  resetVersionHistory: (state: IAIImageState) => {
    state.versionHistory.items = []
    state.versionHistory.isLoading = false
    state.versionHistory.isLoaded = false
  },

  setShowVersionHistoryTab: (
    state: IAIImageState,
    action: PayloadAction<boolean>
  ) => {
    state.showVersionHistoryTab = action.payload
  }
}

export default reducers
